<script setup lang="ts">
import { computed } from 'vue'

import { useComputedAttrs, useComputedInput, useEventsToValidate } from './helpers'
import { BooleanInputProps } from './types'

const props = defineProps<BooleanInputProps>()
const emit = defineEmits(['update:modelValue', 'change', 'blur', 'validate', 'invalid'])
const input = useComputedInput(props, emit)
const attrs = useComputedAttrs(props, 'boolean')
const events = useEventsToValidate(emit)

defineOptions({
  inheritAttrs: false,
})

const booleanOptions = computed(() => {
  if ('options' in props && props.options?.length === 2) {
    return props.options
  } else {
    return [
      {
        label: 'Yes',
        value: true,
      },
      {
        label: 'No',
        value: false,
      },
    ]
  }
})
</script>

<template>
  <div class="boolean-switch" v-for="option in booleanOptions" :key="`${attrs.id}-${option.label}`">
    <input
      class="sr-only"
      v-model="input"
      v-autofocus="props.autofocus"
      v-bind="attrs"
      :id="`${attrs.id}-${option.label}`"
      :value="option.value"
      :checked="option.value === input"
      type="radio"
      :aria-required="props.required"
      :aria-invalid="props.invalid"
      v-on="events"
    />
    <label class="label" :for="`${attrs.id}-${option.label}`">
      {{ option.label }}
    </label>
  </div>
</template>

<style scoped>
.boolean-switch {
  flex: 1;
  display: flex;
  min-width: 0;

  &:first-child .label {
    border-radius: calc(var(--radius-small) - 1px) 0 0 calc(var(--radius-small) - 1px);
    border-right: 1px solid var(--form-boolean-border);
  }

  &:last-child .label {
    border-radius: 0 calc(var(--radius-small) - 1px) calc(var(--radius-small) - 1px) 0;
  }

  input {
    &:checked + .label {
      background-color: var(--form-boolean-on-bg);
    }

    &:focus-visible + .label {
      outline: 2px solid var(--c-input-border-focus);
      outline-offset: 2px;
    }

    &:disabled + .label {
      cursor: default;
      opacity: 0.6;

      &:hover {
        background-color: var(--form-boolean-off-bg-hover);
      }
    }
  }

  .label {
    /* --_boolean-switch-height is inherited from FormInput; variable checker does not follow parent. */
    --_input-font-size: var(--_input-font-size, var(--input-font-size-md));
    width: 100%;
    height: 3em;
    min-width: 4em;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 0 1em;
    font-family: var(--font-family-body);
    font-weight: 600;
    font-size: var(--_input-font-size);
    line-height: 1;
    background-color: var(--form-boolean-off-bg);
    color: var(--form-boolean-text);
    text-align: center;
    cursor: pointer;
    transition:
      background-color 0.2s ease,
      color 0.2s ease;

    &:hover {
      background-color: var(--form-boolean-off-bg-hover);
    }
  }
}
</style>
